import React, { useMemo, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Restaurant } from '../types/restaurant';
import { getCurrentLanguage } from '../config/languageConfig';
import useTranslation from '../hooks/useTranslation';
import { formatPrice as formatPriceWithCurrency, SupportedCurrency, getCurrencyFromLanguage } from '../utils/currencyUtils';
import { getDeliveryStatus, DeliveryStatus } from '../utils/deliveryUtils';

interface RestaurantItemProps {
  restaurant: Restaurant;
  disableNavigation?: boolean;
  currency?: SupportedCurrency;
  isFromCategory?: boolean;
}

const RestaurantItem: React.FC<RestaurantItemProps> = ({ 
  restaurant, 
  disableNavigation = false, 
  currency,
  isFromCategory = false
}) => {
  const navigate = useNavigate();
  const [currentLang, setCurrentLang] = useState(getCurrentLanguage());
  const [deliveryStatus, setDeliveryStatus] = useState<DeliveryStatus>({ canOrderNow: false, canOrderLater: false });
  const { t } = useTranslation();
  
  // 배달 상태 조회
  useEffect(() => {
    const fetchDeliveryStatus = async () => {
      try {
        const status = await getDeliveryStatus();
        setDeliveryStatus(status);
      } catch (error) {
        console.warn('[RestaurantItem] Failed to get delivery status:', error);
      }
    };
    
    fetchDeliveryStatus();
  }, []);
  
  // 현재 언어에 맞는 통화 설정
  const effectiveCurrency = useMemo(() => {
    return getCurrencyFromLanguage(currentLang);
  }, [currentLang]);
  
  // 언어 변경 감지를 위한 이벤트 리스너 추가
  useEffect(() => {
    const handleLanguageChange = () => {
      setCurrentLang(getCurrentLanguage());
    };
    
    // 언어 변경 이벤트 리스너 등록
    window.addEventListener('language-changed', handleLanguageChange);
    
    // 컴포넌트 언마운트 시 이벤트 리스너 제거
    return () => {
      window.removeEventListener('language-changed', handleLanguageChange);
    };
  }, []);
  
  // 현재 언어에 맞는 이름과 설명 계산
  const { displayName, displayDescription } = useMemo(() => {
    // 카테고리 목록에서만 프랜차이즈명 우선 사용 (언어와 관계없이 영문으로 표시)
    if (isFromCategory && restaurant.franchiseName) {
      return {
        displayName: restaurant.franchiseName, // franchiseName은 항상 영문으로 표시
        displayDescription: restaurant.introductionTitle || ''
      };
    }
    
    // 기본값 설정
    let name = restaurant.nameEn || '[Shop Name]';
    let description = restaurant.introductionTitle || '';

    // 현재 언어에 따라 적절한 필드 선택
    switch (currentLang) {
      case 'ko':
        return {
          displayName: restaurant.nameKo || restaurant.nameEn || name,
          displayDescription: restaurant.introductionTitleKo || restaurant.introductionTitleEn || description
        };
      case 'ja':
        return {
          displayName: restaurant.nameJa || restaurant.nameEn || name,
          displayDescription: restaurant.introductionTitleJa || restaurant.introductionTitleEn || description
        };
      case 'zh-CN':
        return {
          displayName: restaurant.nameZhCn || restaurant.nameEn || name,
          displayDescription: restaurant.introductionTitleZhCn || restaurant.introductionTitleEn || description
        };
      case 'zh-TW':
        return {
          displayName: restaurant.nameZhTw || restaurant.nameEn || name,
          displayDescription: restaurant.introductionTitleZhTw || restaurant.introductionTitleEn || description
        };
      case 'en':
      default:
        return {
          displayName: restaurant.nameEn || name,
          displayDescription: restaurant.introductionTitleEn || description
        };
    }
  }, [restaurant, currentLang, isFromCategory]);
  
  // 통화에 맞는 최소 주문 금액 필드 결정
  const getMinOrderAmountByCurrency = useMemo(() => {
    switch (effectiveCurrency) {
      case 'KRW':
        return restaurant.minOrderAmountKrw !== undefined ? restaurant.minOrderAmountKrw : restaurant.minOrderAmount;
      case 'USD':
        return restaurant.minOrderAmountUsd !== undefined ? restaurant.minOrderAmountUsd : restaurant.minOrderAmount;
      case 'JPY':
        return restaurant.minOrderAmountJpy !== undefined ? restaurant.minOrderAmountJpy : restaurant.minOrderAmount;
      case 'CNY':
        return restaurant.minOrderAmountCny !== undefined ? restaurant.minOrderAmountCny : restaurant.minOrderAmount;
      case 'TWD':
        return restaurant.minOrderAmountTwd !== undefined ? restaurant.minOrderAmountTwd : restaurant.minOrderAmount;
      default:
        return restaurant.minOrderAmount;
    }
  }, [restaurant, effectiveCurrency]);
  
  // 최소 주문 금액 표시 텍스트
  const minOrderText = getMinOrderAmountByCurrency > 0 
    ? `${t('restaurant.minOrder')}: ${formatPriceWithCurrency(getMinOrderAmountByCurrency, effectiveCurrency)}` 
    : t('restaurant.noMinimumOrder');
    
  // 가게 상세 페이지로 이동
  const handleClick = () => {
    if (disableNavigation) return;
    navigate(`/restaurant/${restaurant.id}`);
  };

  return (
    <div 
      className={`border rounded-lg overflow-hidden mb-4 bg-white shadow-sm ${!disableNavigation ? 'cursor-pointer' : ''}`}
      onClick={handleClick}
    >
      {/* 레스토랑 이미지 */}
      <div className="h-48 overflow-hidden">
        <img 
          src={restaurant.backgroundUrl || restaurant.logoUrl} 
          alt={displayName}
          className="w-full h-full object-cover"
        />
      </div>
      
      {/* 레스토랑 정보 */}
      <div className="p-3">
        {/* 가게 이름 (현재 언어 우선) */}
        <h3 className="text-xl font-bold mb-2 text-left">
          {displayName}
        </h3>
        
        {/* 가게 설명 (현재 언어 우선, 한 줄로 제한, 말줄임표 적용) */}
        <p className="text-gray-500 mb-4 overflow-hidden whitespace-nowrap text-ellipsis text-sm">
          {displayDescription}
        </p>
        
        {/* 하단 정보 영역 */}
        <div className="flex justify-between items-center mt-2">
          {/* 배달 뱃지 영역 (좌측 하단) - Within 1 hour 자리 대체 */}
          <div className="flex space-x-1">
            {deliveryStatus.canOrderNow && (
              <div className="inline-flex items-center bg-emerald-50 gap-0.5 border border-emerald-200 px-1.5 py-1 rounded-md text-[10px] font-medium text-emerald-700">
                <span className="text-[10px]">⚡</span>
                <span>{t('badge.orderNow')}</span>
              </div>
            )}
            {/* UI 미노출: Later Today 뱃지 - 예약주문 기능 일시적 미노출 */}
            {/* {deliveryStatus.canOrderLater && (
              <div className="inline-flex items-center bg-blue-50 gap-0.5 border border-blue-200 px-1.5 py-1 rounded-md text-[10px] font-medium text-blue-700">
                <span className="text-[10px]">🕐</span>
                <span>{t('badge.orderLater')}</span>
              </div>
            )} */}
          </div>
          
          {/* 최소 주문 금액 (우측 하단) - 선택된 통화로 표시 */}
          <div className="text-sm text-gray-600 text-right font-medium">
            {minOrderText}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RestaurantItem; 